import * as React from "react"
import { Link } from "react-router-dom"
import { BatteryCharging, BatteryLow, BatteryMedium, ChevronLeft, RefreshCw, Search, Tablet, Wifi, WifiOff, X } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { api, type Device, type DeviceStatus, sendCommand } from "@/lib/api"
import { formatRelative } from "@/lib/format"
import { useDevicesStream } from "@/lib/useDevicesStream"

export function DashboardPage() {
  const [devices, setDevices] = React.useState<Device[] | null>(null)
  const [refreshing, setRefreshing] = React.useState(false)
  const [query, setQuery] = React.useState("")

  const load = React.useCallback(async () => {
    try {
      const rows = await api<Device[]>("/api/devices")
      setDevices(rows)
    } catch (err) {
      toast.error(`טעינה נכשלה: ${(err as Error).message}`)
    }
  }, [])

  React.useEffect(() => { load() }, [load])

  useDevicesStream(React.useCallback((event, data) => {
    if (event === "device:connected" || event === "device:disconnected" || event === "device:hello") {
      load()
    } else if (event === "device:status") {
      const d = data as { deviceId: string; status: DeviceStatus }
      setDevices((prev) =>
        prev
          ? prev.map((row) =>
              row.deviceId === d.deviceId
                ? { ...row, lastStatus: d.status, lastStatusAt: new Date().toISOString() }
                : row
            )
          : prev
      )
    }
  }, [load]))

  const filtered = React.useMemo(() => {
    if (!devices) return null
    const q = query.trim().toLowerCase()
    if (!q) return devices
    return devices.filter((d) =>
      [d.displayLabel, d.customLabel, d.reportedLabel, d.deviceId, d.lastStatus?.model, d.notes]
        .some((v) => v && v.toLowerCase().includes(q))
    )
  }, [devices, query])

  async function pingAll() {
    if (!devices) return
    setRefreshing(true)
    const online = devices.filter((d) => d.online)
    await Promise.allSettled(
      online.map((d) => sendCommand(d.deviceId, "get_status").catch(() => null))
    )
    setRefreshing(false)
    await load()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">מכשירים</h1>
          <p className="text-sm text-muted-foreground">
            {devices == null
              ? "טוען…"
              : `${devices.filter((d) => d.online).length} מחוברים מתוך ${devices.length}`}
            {" · "}
            <span>לחיצה על כרטיס פותחת את עמוד המכשיר</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="size-3.5" />
            רענן
          </Button>
          <Button size="sm" onClick={pingAll} disabled={refreshing || !devices?.some((d) => d.online)}>
            <RefreshCw className={refreshing ? "size-3.5 animate-spin" : "size-3.5"} />
            עדכן סטטוס מכולם
          </Button>
        </div>
      </div>

      {devices && devices.length > 0 && (
        <div className="relative">
          <Search className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="חיפוש לפי שם, מזהה או מודל…"
            className="pe-9 ps-9"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute start-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label="נקה חיפוש"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      )}

      {devices == null ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-24 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : devices.length === 0 ? (
        <EmptyState />
      ) : filtered && filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            לא נמצאו מכשירים התואמים ל־"{query}".
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered!.map((d) => (
            <DeviceCard key={d.deviceId} device={d} />
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
        <div className="rounded-full bg-muted p-3">
          <Tablet className="size-6 text-muted-foreground" />
        </div>
        <div>
          <div className="font-semibold">אין מכשירים רשומים</div>
          <p className="text-sm text-muted-foreground">
            מכשיר שיתחבר ל-<code className="rounded bg-muted px-1.5 py-0.5">/api/connect-devices</code> יופיע כאן.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

function DeviceCard({ device }: { device: Device }) {
  const batteryPct = device.lastStatus?.battery?.percent
  const charging = device.lastStatus?.battery?.charging
  const model = device.lastStatus?.model
  const appVersion = device.lastStatus?.appVersion
  const lastSeen = device.online ? null : device.lastDisconnectedAt || device.lastConnectedAt

  return (
    <Link to={`/devices/${encodeURIComponent(device.deviceId)}`} className="block">
      <Card className="transition hover:border-primary/40 hover:shadow-md">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-semibold">{device.displayLabel}</span>
              </div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {model || "-"} · {appVersion || ""}
              </div>
            </div>
            <Badge variant={device.online ? "success" : "outline"}>
              {device.online ? (
                <>
                  <Wifi className="size-3" />
                  מחובר
                </>
              ) : (
                <>
                  <WifiOff className="size-3" />
                  לא מחובר
                </>
              )}
            </Badge>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-1.5">
              <BatteryIcon percent={batteryPct} charging={charging} />
              <span>{batteryPct != null ? `${Math.round(batteryPct)}%` : "-"}</span>
            </div>
            <div className="text-end text-xs text-muted-foreground">
              {device.online
                ? `התחבר ${formatRelative(device.lastConnectedAt)}`
                : `נראה ${formatRelative(lastSeen)}`}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-mono">{device.deviceId.slice(0, 12)}…</span>
            <span className="inline-flex items-center gap-1">
              פרטים
              <ChevronLeft className="size-3.5" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

function BatteryIcon({ percent, charging }: { percent?: number; charging?: boolean }) {
  if (charging) return <BatteryCharging className="size-4 text-emerald-600" />
  if (percent == null) return <BatteryMedium className="size-4 text-muted-foreground" />
  if (percent < 20) return <BatteryLow className="size-4 text-rose-600" />
  return <BatteryMedium className="size-4 text-muted-foreground" />
}
